import React, { useMemo, useEffect, useState, useContext } from 'react';

import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useFormik } from 'formik';
import yupSchemaToFields from '../../../../../../helpers/yupSchemaToFields';

import SidePopup from '../../../SidePopup';
import TextInput from '../../../../../Form/TextInput';
import PhoneInput from '../../../../../Form/PhoneInput';

import classes from './styles.module.scss';
import { UiContext } from '../../../../../../context/UiContext';
import AdminService from '../../../../../../services/AdminService';
import useSwitchableRowData from '../../../../../../hooks/useSwitchableRowData';

export default function InterviewerSidePanel({
  isVisible,
  isEdit,
  handleClose,
  interviewers,
  selectedRows,
  refetch,
}) {
  const { t } = useTranslation();
  const [hasTriedToSubmit, setHasTriedToSubmit] = useState(false);

  const { currentDataIndex, switchToPrevDataElement, switchToNextDataElement } =
    useSwitchableRowData(interviewers, selectedRows);

  const {
    showModal,
    showUnknownErrorModal,
    setIsFetching,
    isCreatingOrUpdating,
    setIsCreatingOrUpdating,
  } = useContext(UiContext);

  const validationSchema = useMemo(
    () =>
      yup.object({
        firstName: yup
          .string()
          .trim()
          .matches(
            /^[A-Za-z^]*$/,
            t(
              'dashboardComponents.Console.ExternalInterviewers.addEdit.errorMessages.specialCharactersNotAllowed'
            )
          )
          .required(
            t(
              'dashboardComponents.Console.ExternalInterviewers.addEdit.errorMessages.required'
            )
          ),
        lastName: yup
          .string()
          .trim()
          .matches(
            /^[A-Za-z^]*$/,
            t(
              'dashboardComponents.Console.ExternalInterviewers.addEdit.errorMessages.specialCharactersNotAllowed'
            )
          )
          .required(
            t(
              'dashboardComponents.Console.ExternalInterviewers.addEdit.errorMessages.required'
            )
          ),
        email: yup
          .string()
          .trim()
          .email(
            t(
              'dashboardComponents.Console.ExternalInterviewers.addEdit.errorMessages.validEmail'
            )
          )
          .required(
            t(
              'dashboardComponents.Console.ExternalInterviewers.addEdit.errorMessages.required'
            )
          ),
        designation: yup
          .string()
          .trim()
          .nullable()
          .optional()
          .max(
            100,
            t(
              'dashboardComponents.Console.ExternalInterviewers.addEdit.errorMessages.designationMaxLength'
            )
          ),
        phone: yup
          .string()
          .required(
            t(
              'dashboardComponents.Console.ExternalInterviewers.addEdit.errorMessages.required'
            )
          )
          .test(
            'is-valid-phone',
            t(
              'dashboardComponents.Console.ExternalInterviewers.addEdit.errorMessages.phoneInvalid'
            ),
            async (value) => {
              if (!value) {
                return true; // Skip validation if the value is empty
              }

              const { isValidPhoneNumber, AsYouType } = await import(
                'libphonenumber-js'
              );

              const formattedNumber = new AsYouType().input(value);

              const formattedNumberWithPlus = formattedNumber.startsWith('+')
                ? formattedNumber
                : `+${formattedNumber}`;

              return isValidPhoneNumber(
                new AsYouType().input(formattedNumberWithPlus)
              );
            }
          ),
      }),
    [t]
  );

  const formik = useFormik({
    initialValues: isEdit
      ? {
          firstName: interviewers?.[currentDataIndex]?.firstName || '',
          lastName: interviewers?.[currentDataIndex]?.lastName || '',
          email: interviewers?.[currentDataIndex]?.email || '',
          phone: interviewers?.[currentDataIndex]?.phone || '',
          designation: interviewers?.[currentDataIndex]?.designation || '',
        }
      : {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          designation: '',
        },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsFetching(true);
        setIsCreatingOrUpdating(true);

        const requestData = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          id: isEdit ? interviewers[currentDataIndex].id : null,
          designation: values.designation,
        };

        if (isEdit) {
          await AdminService.updateExternalInterviewer(requestData);
        } else {
          await AdminService.createExternalInterviewer(requestData);
        }

        await refetch();
        showModal({
          title: t(
            isEdit
              ? 'dashboardComponents.Console.ExternalInterviewers.EditInterviewer.successModal.title'
              : 'dashboardComponents.Console.ExternalInterviewers.AddInterviewer.successModal.title'
          ),
          text: t(
            isEdit
              ? 'dashboardComponents.Console.ExternalInterviewers.EditInterviewer.successModal.text'
              : 'dashboardComponents.Console.ExternalInterviewers.AddInterviewer.successModal.text'
          ),
          onCancel: handleClose,
        });
      } catch (error) {
        console.log(error);
        showUnknownErrorModal();
      } finally {
        setIsFetching(false);
        setIsCreatingOrUpdating(false);
      }
    },
    enableReinitialize: true,
  });

  // Ensure add-mode form is empty on open
  useEffect(() => {
    if (isVisible && !isEdit) {
      setHasTriedToSubmit(false);
      formik.resetForm({
        values: {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          designation: '',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, isEdit]);

  const onClose = () => {
    setHasTriedToSubmit(false);
    handleClose();
  };

  const resetForm = () => {
    formik.resetForm();
    setHasTriedToSubmit(false);
  };

  return (
    <SidePopup
      fields={[...yupSchemaToFields(validationSchema)]}
      formik={{ ...formik, resetForm, dirty: formik.dirty }}
      mode={isEdit ? 'edit' : 'add'}
      isVisible={isVisible}
      handleClose={onClose}
      title={
        isEdit
          ? t(
              'dashboardComponents.Console.ExternalInterviewers.addEdit.titleEdit'
            )
          : t(
              'dashboardComponents.Console.ExternalInterviewers.addEdit.titleAdd'
            )
      }
      isRightButtonDisabled={
        Object.values(formik.values).some((val) => !val) || isCreatingOrUpdating
      }
      leftButtonLabel={t('common.reset')}
      rightButtonLabel={isEdit ? t('common.save') : t('common.add')}
      onLeftButtonClick={resetForm}
      switchNext={selectedRows.length > 1 && switchToNextDataElement}
      switchPrev={selectedRows.length > 1 && switchToPrevDataElement}
      onRightButtonClick={() => {
        setHasTriedToSubmit(true);
        formik.handleSubmit();
      }}
    >
      <div className={classes.ManagerSidePanel}>
        <div className={classes.row}>
          <div className={classes.col}>
            <TextInput
              label={t(
                'dashboardComponents.Console.ExternalInterviewers.addEdit.labels.firstName'
              )}
              touched={hasTriedToSubmit}
              value={formik.values.firstName}
              height={50}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="firstName"
              error={formik.errors.firstName}
            />
          </div>
          <div className={classes.col}>
            <TextInput
              label={t(
                'dashboardComponents.Console.ExternalInterviewers.addEdit.labels.lastName'
              )}
              value={formik.values.lastName}
              height={50}
              touched={hasTriedToSubmit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="lastName"
              error={formik.errors.lastName}
            />
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.col}>
            <TextInput
              label={t(
                'dashboardComponents.Console.ExternalInterviewers.addEdit.labels.designation'
              )}
              touched={hasTriedToSubmit}
              value={formik.values.designation}
              height={50}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="designation"
              error={formik.errors.designation}
              note={t('common.optional')}
            />
          </div>
          <div className={classes.col}>
            <PhoneInput
              name="phone"
              touched={hasTriedToSubmit}
              label={t(
                'dashboardComponents.Console.ExternalInterviewers.addEdit.labels.phone'
              )}
              error={formik.errors.phone}
              setFieldValue={formik.setFieldValue}
              value={formik.values.phone}
              height={50}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.col}>
            <TextInput
              label={t(
                'dashboardComponents.Console.ExternalInterviewers.addEdit.labels.email'
              )}
              touched={hasTriedToSubmit}
              value={formik.values.email}
              height={50}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="email"
              error={formik.errors.email}
            />
          </div>
        </div>
      </div>
    </SidePopup>
  );
}
